import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DatabaseUserDocumentType, UserDocument } from '../../clients/quipli-service/user/user';
import { DatePicker } from '../../components/date-picker/date-picker';
import { Field, FileField } from '../../components/field/field';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { InsuranceDocument, InsuranceDocumentRequest } from '../../clients/customer-api/customer/customer-customer';
import { EcommerceButton } from '../../components/form/button';
import { LegacyDownloadLink } from '../../components/link/legacy-download-link';
import Icon from '../../components/icons/icon';
import './insurance-form.scss';
import { Card } from '../../components/card/card';
import { Divider } from '../../components/divider/divider';
import { useCustomer } from '../../hooks/use-customer-context';

export const InsuranceForm = ({
  insuranceDocument,
  hideFile,
  closeModal,
  addInsuranceDocument,
  updateInsuranceDocument,
  onSubmit,
  setRootAttributes,
}: {
  insuranceDocument?: InsuranceDocument;
  hideFile?: boolean;
  closeModal?: () => void;
  addInsuranceDocument: (body: Omit<InsuranceDocumentRequest, 'userId'>) => Promise<InsuranceDocument | undefined>;
  updateInsuranceDocument: (
    documentId: number,
    body: Omit<InsuranceDocumentRequest, 'userId'>,
  ) => Promise<InsuranceDocument | undefined>;
  onSubmit?: () => void;
  setRootAttributes?: (o: any) => void;
}) => {
  const customer = useCustomer();

  const { createUpdateUserDocument } = useCustomerAPI();
  const [file, setFile] = useState<File>();
  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [insuranceState, setInsuranceState] = useState<{
    firstName?: string;
    lastName?: string;
    issuedBy?: string;
    policyNumber?: string;
    coverageAmount?: number;
    file?: UserDocument;
  }>(
    insuranceDocument ?? {
      firstName: '',
      lastName: '',
      issuedBy: '',
      policyNumber: '',
      coverageAmount: undefined,
      file: undefined,
    },
  );
  const [expiryDate, setExpiryDate] = useState<DateTime | undefined>(insuranceDocument?.expiryDate);
  const [issuedDate, setIssuedDate] = useState<DateTime | undefined>(insuranceDocument?.issuedDate);

  const addDocument = async () => {
    if (!customer) return;

    // Block buttons and exit of modal
    setRequestInProgress(true);
    const isEdit =
      !!insuranceDocument?.id &&
      insuranceDocument.issuedBy === insuranceState.issuedBy &&
      insuranceDocument.policyNumber === insuranceState.policyNumber &&
      insuranceDocument.expiryDate!.toISODate() === expiryDate?.toISODate() &&
      insuranceDocument.issuedDate!.toISODate() === issuedDate?.toISODate() &&
      insuranceDocument.coverageAmount === insuranceState.coverageAmount;
    let newFileId;
    if (file) {
      try {
        const newFile = await createUpdateUserDocument(
          file,
          customer.id.toString(),
          DatabaseUserDocumentType.Insurance.toString(),
          isEdit,
        );
        newFileId = newFile?.fileId;
        //verify size
        if (setRootAttributes) {
          setRootAttributes({
            'data-saved': `true`,
          });
        }
      } catch (error) {
        toast.error('Error uploading file');
        return;
      }
    }
    if (
      insuranceState.firstName &&
      insuranceState.lastName &&
      insuranceState.issuedBy &&
      insuranceState.policyNumber &&
      expiryDate &&
      issuedDate
    ) {
      try {
        const body: Omit<InsuranceDocumentRequest, 'userId'> = {
          firstName: insuranceState.firstName,
          lastName: insuranceState.lastName,
          issuedBy: insuranceState.issuedBy,
          policyNumber: insuranceState.policyNumber,
          expiryDate: expiryDate,
          issuedDate: issuedDate,
          coverageAmount: insuranceState.coverageAmount,
          userFileId: newFileId ?? insuranceDocument?.file?.id,
        };
        if (isEdit && insuranceDocument?.id) {
          await updateInsuranceDocument(insuranceDocument!.id, body);
          if (setRootAttributes) {
            setRootAttributes({
              'data-saved': `true`,
            });
          }
        } else if (body.expiryDate) {
          await addInsuranceDocument(body);
          if (setRootAttributes) {
            setRootAttributes({
              'data-saved': `true`,
            });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error('Error saving insurance document');
        setRequestInProgress(false);
        if (setRootAttributes) {
          setRootAttributes({
            'data-saved': `false`,
          });
        }

        return;
      } finally {
        setRequestInProgress(false);
      }
    } else {
      setRequestInProgress(false);
    }
    onSubmit?.();
  };

  const isButtonDisabled = () => {
    return (
      isRequestInProgress ||
      (file &&
        (insuranceState.firstName ||
          insuranceState.lastName ||
          insuranceState.issuedBy ||
          insuranceState.policyNumber ||
          expiryDate ||
          issuedDate) &&
        !(
          insuranceState.firstName &&
          insuranceState.lastName &&
          insuranceState.issuedBy &&
          insuranceState.policyNumber &&
          expiryDate &&
          issuedDate
        )) ||
      (!file &&
        (!insuranceState.firstName ||
          !insuranceState.lastName ||
          !insuranceState.issuedBy ||
          !insuranceState.policyNumber ||
          !expiryDate ||
          !issuedDate))
    );
  };

  useEffect(() => {
    if (insuranceDocument) {
      setInsuranceState(insuranceDocument);
    }
  }, [insuranceDocument]);
  return (
    <>
      <div className="py-4 px-3">
        <Card className="max-w-md bg-grey-25 !rounded-lg p-3 flex flex-col gap-2 insurance-form">
          <div className="flex gap-2">
            <Field
              type="text"
              isRequired={!file}
              label="First Name"
              value={insuranceState.firstName}
              onChange={(firstName: string) => setInsuranceState({ ...insuranceState, firstName })}
            />
            <Field
              type="text"
              isRequired={!file}
              label="Last Name"
              value={insuranceState.lastName}
              onChange={(lastName: string) => setInsuranceState({ ...insuranceState, lastName })}
            />
          </div>
          <Field
            className="my-2"
            type="text"
            isRequired={!file}
            label="Issued By"
            value={insuranceState.issuedBy}
            onChange={(issuedBy: string) => setInsuranceState({ ...insuranceState, issuedBy })}
          />
          <Field
            className="mb-2"
            type="text"
            isRequired={!file}
            label="Policy Number"
            value={insuranceState.policyNumber}
            onChange={(policyNumber: string) => setInsuranceState({ ...insuranceState, policyNumber })}
          />
          <DatePicker
            label="Expiry Date"
            isRequired={!file}
            value={expiryDate}
            onChange={(expiryDate) => setExpiryDate(expiryDate)}
          />
          <DatePicker
            isRequired={!file}
            label="Issued Date"
            value={issuedDate}
            onChange={(issuedDate) => setIssuedDate(issuedDate)}
          />
          <Field
            className="mb-2"
            type="currency"
            label="Coverage Amount"
            value={insuranceState.coverageAmount}
            onChange={(coverageAmount: number) => setInsuranceState({ ...insuranceState, coverageAmount })}
          />

          {!hideFile && (
            <>
              <Divider text="OR" />
              <FileField
                className="w-full mb-2"
                label="Upload file"
                value={file}
                onChange={(newFile) => setFile(newFile as File)}
                id="insurance-file"
              />
              {insuranceDocument?.file && (
                <LegacyDownloadLink
                  className="w-4/5 flex justify-between mt-2"
                  href={`users/download_file/${customer?.id}/${
                    DatabaseUserDocumentType[insuranceDocument.file.documentType]
                  }`}
                >
                  <span className="text-document-overflow">{insuranceDocument.file.fileName}</span>
                  <Icon icon="Download" size="medium" />
                </LegacyDownloadLink>
              )}{' '}
            </>
          )}
        </Card>
      </div>

      <Divider />

      <div className="flex justify-start pt-4 px-3 gap-2">
        {closeModal && (
          <EcommerceButton buttonStyle="tertiary" disabled={isRequestInProgress} onClick={() => closeModal()}>
            Cancel
          </EcommerceButton>
        )}

        <EcommerceButton
          id="save-insurance-button"
          buttonStyle="primary"
          disabled={isButtonDisabled()}
          onClick={() => {
            addDocument();
          }}
          dataCy="save_insurance"
        >
          Save
        </EcommerceButton>
      </div>
    </>
  );
};
